import { Injectable } from '@angular/core';
import {
  MotionMasterClientIndexedDB,
  extractEsiFromFirmwarePackage,
  extractFirmwareVersionFromFirmwarePackageFilename,
} from 'motion-master-client';

/**
 * Caches ESI (EtherCAT Slave Information) files into the browser's IndexedDB so they can be
 * resolved locally without writing them to the drive.
 *
 * This mirrors what `installFirmwareAndCacheEsi` does at install time, but lets the user
 * seed the cache directly from a firmware package file (the "Load ESI" action).
 */
@Injectable({
  providedIn: 'root',
})
export class EsiCacheService {
  private idb = new MotionMasterClientIndexedDB();

  /**
   * Extracts the ESI from a firmware package and stores it in IndexedDB keyed by the firmware
   * version derived from the package filename.
   *
   * @param filename - The firmware package filename (used to derive the firmware version key).
   * @param buffer - The firmware package (ZIP archive) content.
   * @returns The firmware version the ESI was cached under.
   * @throws If the firmware version cannot be derived from the filename or the package contains no ESI.
   */
  async cacheEsiFromFirmwarePackage(filename: string, buffer: ArrayBuffer): Promise<string> {
    const version = extractFirmwareVersionFromFirmwarePackageFilename(filename);
    if (!version) {
      throw new Error(`Could not determine the firmware version from "${filename}".`);
    }

    const esi = extractEsiFromFirmwarePackage(new Uint8Array(buffer));
    if (!esi) {
      throw new Error(`The firmware package "${filename}" does not contain an ESI file.`);
    }

    await this.idb.open();
    await this.idb.putEsi(version, esi);

    return version;
  }
}
